import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { SingleDatePicker } from 'legacyfrontend/components';
import FormField from '../../shared/FormField';
import ErrorSnackbar from '../../ErrorSnackbar';
import ConsultasClient from '../../../api/consultas';
import {
  getRegretFormData,
  getRegretFormStatus,
} from 'legacyfrontend/redux/selectors/forms-selectors';
import {
  setRegretFormData,
  setRegretFormStatus,
  resetRegretForm,
} from 'legacyfrontend/redux/actions/forms-actions';
import { useFormToast } from 'legacyfrontend/hooks/useToast';

function FormularioArrepentimiento() {
  const history = useHistory();
  const dispatch = useDispatch();

  const applicationState = useSelector((state) => state.applicationState);
  const regretForm = useSelector(getRegretFormData);
  const regretSubmissionStatus = useSelector(getRegretFormStatus);

  const [showModal, setShowModal] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const { showToast, hideToast, toastState } = useFormToast();

  useEffect(() => {
    if (!regretForm.numeroCompra) {
      dispatch(
        setRegretFormData({
          numeroCompra: '',
          fechaCompra: null,
          comentarios: '',
          nombre: '',
          apellido: '',
          email: '',
          telefono: '',
          tipoConsulta: 'arrepentimiento',
          agenciaId: parseInt(applicationState.applicationId) || 0,
          paisId: parseInt(applicationState.paisId) || 0,
          nombreAgencia: applicationState.nombre || '',
          emailAgencia: applicationState.templateData?.header?.emailContacto || '',
        })
      );
    }
  }, [dispatch, regretForm.numeroCompra, applicationState]);

  useEffect(() => {
    switch (regretSubmissionStatus) {
      case 'success':
        setShowModal(true);
        setTimeout(() => {
          setIsRedirecting(true);
          setTimeout(() => {
            history.push('/');
          }, 1000);
        }, 3000);
        break;
      case 'error':
        setShowModal(true);
        break;
      default:
        break;
    }
  }, [regretSubmissionStatus, history]);

  const handleInputChange = useCallback(
    (event) => {
      const { name, value } = event.target;
      dispatch(setRegretFormData({ [name]: value }));
    },
    [dispatch]
  );

  const setearFechaCompra = useCallback(
    (fecha) => {
      dispatch(setRegretFormData({ fechaCompra: fecha }));
    },
    [dispatch]
  );

  const isFormValid = useMemo(() => {
    return (
      regretForm.numeroCompra &&
      regretForm.fechaCompra &&
      regretForm.comentarios &&
      regretForm.nombre &&
      regretForm.apellido &&
      regretForm.email &&
      regretForm.telefono
    );
  }, [regretForm]);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      if (!isFormValid) {
        showToast('Por favor completa todos los campos requeridos', 'error');
        return;
      }

      dispatch(setRegretFormStatus('sending'));

      try {
        const token = applicationState.token;

        const formData = {
          ...regretForm,
          tipoConsulta: 'arrepentimiento',
          agenciaId: parseInt(applicationState.applicationId) || 0,
          paisId: parseInt(applicationState.paisId) || 0,
          nombreAgencia: applicationState.nombre || '',
          emailAgencia: applicationState.templateData?.header?.emailContacto || '',
        };

        const consultaResponse = await ConsultasClient.consultaMarcaBlanca(formData, token);

        if (consultaResponse.status === 201) {
          const formWithId = { ...formData, id: consultaResponse.data.id };
          const emailResponse = await ConsultasClient.enviarMailArrepentimiento(formWithId, token);

          if (emailResponse.status === 201) {
            dispatch(setRegretFormStatus('success'));
          } else {
            dispatch(setRegretFormStatus('error'));
          }
        } else {
          dispatch(setRegretFormStatus('error'));
        }
      } catch (error) {
        console.error('Error al enviar arrepentimiento:', error);
        dispatch(setRegretFormStatus('error'));
      }
    },
    [isFormValid, regretForm, applicationState, dispatch, showToast]
  );

  const handleRetry = useCallback(() => {
    setShowModal(false);
    dispatch(setRegretFormStatus('idle'));
  }, [dispatch]);

  const handleCancel = useCallback(() => {
    setShowModal(false);
    dispatch(resetRegretForm());
    history.push('/');
  }, [dispatch, history]);

  return (
    <>
      <div className='min-h-screen py-8'>
        <div className='max-w-4xl mx-auto px-4'>
          <form
            autoComplete='off'
            onSubmit={handleSubmit}
            className='bg-white shadow-lg rounded-lg p-8'
          >
            {/* Header */}
            <div className='mb-8 text-center'>
              <h1 className='text-3xl font-bold text-red-800 mb-2'>
                Formulario de Arrepentimiento
              </h1>
              <p className='text-gray-600'>
                Completa el siguiente formulario para solicitar la cancelación de tu compra
              </p>
            </div>

            {/* Datos de la compra */}
            <div className='mb-8'>
              <h2 className='text-xl font-semibold text-gray-800 mb-6 pb-2 border-b border-gray-200'>
                📋 Datos de la compra
              </h2>
              <div className='grid md:grid-cols-2 gap-6 mb-6'>
                <FormField
                  label='Número de compra'
                  name='numeroCompra'
                  value={regretForm.numeroCompra}
                  onChange={handleInputChange}
                  placeholder='Ej: MB123456789'
                  required
                />
                <div className='flex flex-col'>
                  <SingleDatePicker
                    selectedDate={regretForm.fechaCompra}
                    onChange={setearFechaCompra}
                    label='Fecha de compra *'
                    placeholder='Seleccionar fecha'
                    maxDate={new Date()}
                    dateFormat='dd/MM/yyyy'
                  />
                </div>
              </div>
              <FormField
                label='Descripción del reclamo'
                name='comentarios'
                type='textarea'
                value={regretForm.comentarios}
                onChange={handleInputChange}
                placeholder='Describe detalladamente el motivo del reclamo o cancelación...'
                rows={4}
                required
              />
            </div>

            {/* Datos del cliente */}
            <div className='mb-8'>
              <h2 className='text-xl font-semibold text-gray-800 mb-6 pb-2 border-b border-gray-200'>
                👤 Datos del cliente
              </h2>
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6'>
                <FormField
                  label='Nombre'
                  name='nombre'
                  value={regretForm.nombre}
                  onChange={handleInputChange}
                  placeholder='Tu nombre'
                  required
                />
                <FormField
                  label='Apellido'
                  name='apellido'
                  value={regretForm.apellido}
                  onChange={handleInputChange}
                  placeholder='Tu apellido'
                  required
                />
                <FormField
                  label='E-mail'
                  name='email'
                  type='email'
                  value={regretForm.email}
                  onChange={handleInputChange}
                  placeholder='tu@email.com'
                  required
                />
                <FormField
                  label='Teléfono'
                  name='telefono'
                  type='tel'
                  value={regretForm.telefono}
                  onChange={handleInputChange}
                  placeholder='+54 11 1234-5678'
                  required
                />
              </div>
            </div>

            {/* Botón de envío */}
            <div className='flex justify-center pt-6 border-t border-gray-200'>
              <button
                className={`font-semibold py-3 px-8 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 ${isFormValid
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                type='submit'
                disabled={!isFormValid}
              >
                📤 Enviar reclamo de arrepentimiento
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Overlay de envío que se muestra encima del formulario */}
      {regretSubmissionStatus === 'sending' && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div className='bg-white rounded-lg p-8 max-w-md mx-4 text-center'>
            <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4'></div>
            <h3 className='text-lg font-semibold text-gray-900 mb-2'>Enviando formulario...</h3>
            <p className='text-gray-600'>Por favor espera mientras procesamos tu solicitud.</p>
          </div>
        </div>
      )}

      {/* Modal para confirmación y errores */}
      {showModal && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div className='bg-white rounded-lg p-8 max-w-md mx-4 text-center'>
            {regretSubmissionStatus === 'success' ? (
              <>
                <div className='w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center'>
                  <svg
                    className='w-8 h-8 text-green-600'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M5 13l4 4L19 7'
                    />
                  </svg>
                </div>
                <h3 className='text-lg font-semibold text-gray-900 mb-2'>
                  ¡Formulario enviado exitosamente!
                </h3>
                <p className='text-gray-600 mb-4'>
                  Tu solicitud de arrepentimiento ha sido procesada correctamente.
                </p>
                {isRedirecting ? (
                  <>
                    <div className='animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mx-auto mb-2'></div>
                    <p className='text-sm text-gray-500'>Redirigiendo...</p>
                  </>
                ) : (
                  <p className='text-sm text-gray-500'>
                    Serás redirigido automáticamente en unos segundos.
                  </p>
                )}
              </>
            ) : (
              <>
                <div className='w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center'>
                  <svg
                    className='w-8 h-8 text-red-600'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M6 18L18 6M6 6l12 12'
                    />
                  </svg>
                </div>
                <h3 className='text-lg font-semibold text-gray-900 mb-2'>
                  Error al enviar formulario
                </h3>
                <p className='text-gray-600 mb-6'>
                  Hubo un problema al procesar tu solicitud. Por favor, inténtalo nuevamente.
                </p>
                <div className='flex gap-3 justify-center'>
                  <button
                    onClick={handleRetry}
                    className='bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors'
                  >
                    Reintentar
                  </button>
                  <button
                    onClick={handleCancel}
                    className='bg-gray-300 hover:bg-gray-400 text-gray-700 font-semibold py-2 px-4 rounded-lg transition-colors'
                  >
                    Cancelar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Toast notifications */}
      <ErrorSnackbar
        open={toastState.open}
        message={toastState.message}
        severity={toastState.severity}
        onClose={hideToast}
      />
    </>
  );
}

export default FormularioArrepentimiento;
